import { useId, useState } from 'react';

import { useSession } from '../auth/session-context.js';

/**
 * Workspace switching (ARCHITECTURE.md §13).
 *
 * Choosing a workspace calls the API, which re-verifies membership before it
 * changes anything. The Console then refetches, so no cross-workspace state
 * survives the switch — §13 requires exactly that.
 */
export function WorkspaceSwitcher(): React.JSX.Element | null {
  const { me, activeWorkspace, activateWorkspace } = useSession();
  const id = useId();
  const [switching, setSwitching] = useState(false);

  if (me === null || me.workspaces.length === 0) {
    return null;
  }

  const handleChange = (workspaceId: string): void => {
    if (workspaceId === activeWorkspace?.id) return;
    setSwitching(true);
    void activateWorkspace(workspaceId).finally(() => {
      setSwitching(false);
    });
  };

  return (
    <div className="workspace-switcher">
      <label className="visually-hidden" htmlFor={id}>
        Active workspace
      </label>
      <select
        id={id}
        value={activeWorkspace?.id ?? ''}
        disabled={switching}
        onChange={(event) => {
          handleChange(event.target.value);
        }}
      >
        {activeWorkspace === null ? <option value="">Select a workspace…</option> : null}
        {me.workspaces.map((workspace) => (
          <option key={workspace.id} value={workspace.id}>
            {workspace.name}
          </option>
        ))}
      </select>
      {switching ? (
        <span className="visually-hidden" role="status">
          Switching workspace
        </span>
      ) : null}
    </div>
  );
}
